import React, { useState, useEffect } from "react";
import { Container, Box, Typography, Grid, Link } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import BackgroundShape from "../Assets/Background-Shape.png";

// Custom Icons
import BadgeIcon from "../Assets/Icons/badge.png";
import MapIcon from "../Assets/Icons/floor-plan.png";
import PeopleIcon from "../Assets/Icons/attendees.png";
import ExhibitorsIcon from "../Assets/Icons/exhibitor.png";
import ProductsIcon from "../Assets/Icons/product.png";
import ConferencesIcon from "../Assets/Icons/conference.png";
import NetworkingIcon from "../Assets/Icons/networking.png";
import AgendaIcon from "../Assets/Icons/my-agenda.png";
import SponsorsIcon from "../Assets/Icons/sponsor.png";
import useUserData from "../hooks/useUserData";
import LoadingPage from "./LoadingPage";
import SubHeader from "../components/SubHeader";
import LogoSection from "../components/LogoSection";
import SponsorSection from "../components/SponsorSection";
import SpeakerIcon from "../Assets/Icons/speaker.png";

const Homepage = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { t } = useTranslation();
  const { data } = useUserData();
  const [loading, setLoading] = useState(true);

  let me;
  if (typeof data?.me === "string") {
    me = JSON.parse(data?.me);
  } else {
    me = data?.me;
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const menuItems = [
    {
      id: "myBadge",
      color: "linear-gradient(45deg, #1e3c72, #2a5298)",
      icon: BadgeIcon,
      link: "/demo/my-badge",
    },
    {
      id: "floorPlan",
      color: "linear-gradient(45deg, #ff512f, #dd2476)",
      icon: MapIcon,
      link: "/demo/floor-plan",
    },
    {
      id: "attendees",
      color: "linear-gradient(45deg, #24c6dc, #514a9d)",
      icon: PeopleIcon,
      link: "/demo/attendees",
    },
    {
      id: "exhibitors",
      color: "linear-gradient(45deg, #ff5f6d, #ffc371)",
      icon: ExhibitorsIcon,
      link: "/demo/exhibitors",
    },
    // {
    //   id: "products",
    //   color: "linear-gradient(45deg, #36d1dc, #5b86e5)",
    //   icon: ProductsIcon,
    //   link: "/demo/products",
    // },
    {
      id: "StrategicProgram",
      color: "linear-gradient(45deg, #ff4e50, #f9d423)",
      icon: ConferencesIcon,
      link: "/demo/strategic",
    },
    {
      id: "MunicipalWaterProgram",
      color: "linear-gradient(45deg, #ff4e50, #f9d423)",
      icon: ConferencesIcon,
      link: "/demo/municipal",
    },
    {
      id: "Produced/IndustrialWaterProgram",
      color: "linear-gradient(45deg, #ff4e50, #f9d423)",
      icon: ConferencesIcon,
      link: "/demo/produced",
    },
    {
      id: "AgendaInnovationProgram",
      color: "linear-gradient(45deg, #ff4e50, #f9d423)",
      icon: ConferencesIcon,
      link: "/demo/innovation",
    },
    {
      id: "WaterTalksProgram",
      color: "linear-gradient(45deg, #ff4e50, #f9d423)",
      icon: ConferencesIcon,
      link: "/demo/water-talks",
    },
    {
      id: "HydrogenWorkshopProgram",
      color: "linear-gradient(45deg, #ff4e50, #f9d423)",
      icon: ConferencesIcon,
      link: "/demo/hydrogen",
    },
    {
      id: "speakers",
      color: "linear-gradient(45deg, #00c6ff, #0072ff)",
      icon: SpeakerIcon,
      link: "/demo/speakers",
    },
    {
      id: "networking",
      color: "linear-gradient(45deg, #00c6ff, #0072ff)",
      icon: NetworkingIcon,
      link: "/demo/networking",
    },
    {
      id: "myAgenda",
      color: "linear-gradient(45deg, #f7971e, #ffd200)",
      icon: AgendaIcon,
      link: "/demo/my-agenda",
    },
    {
      id: "sponsors",
      color: "linear-gradient(45deg, #00b09b, #96c93d)",
      icon: SponsorsIcon,
      link: "/demo/sponsors",
    },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    if (me?.role === "visitor") {
      return !["attendees", "networking", "myAgenda"].includes(item.id);
    }
    if (me?.role !== "delegate") {
      return !["myAgenda"].includes(item.id);
    }
    return true;
  });

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: mode === "light" ? "#2a4b50" : "inherit",
        backgroundImage: mode === "light" ? `url(${BackgroundShape})` : "none",
        backgroundBlendMode: "multiply",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        color: "#fff",
      }}
    >
      <Container sx={{ py: { xs: 2, sm: 2 }, px: { xs: 2, sm: 3 } }}>
        <SubHeader />
        <LogoSection />

        <Grid container spacing={{ xs: 2, sm: 2.5 }} sx={{ my: 4 }}>
          {filteredMenuItems.map((item, index) => (
            <Grid
              item
              xs={6}
              sx={{
                "@media (min-width:374px)": {
                  flexBasis: "33.3333%",
                  maxWidth: "33.3333%",
                },
              }}
              key={`${item.link}-${index}`}
            >
              <Link
                href={item.link}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  textDecoration: "none",
                  p: { xs: 1.5, sm: 2 },
                  border: "2px solid rgba(26,169,139, 1)",
                  borderRadius: "8px",
                  height: "100%",
                  minHeight: "110px",
                  backgroundColor: "rgba(76, 212, 192, 0.05)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "rgba(76, 212, 192, 0.1)",
                    transform: "scale(1.02)",
                  },
                }}
              >
                <img
                  src={item.icon}
                  alt={item.id}
                  style={{
                    width: "50px",
                    height: "50px",
                    marginBottom: "8px",
                    objectFit: "contain",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: (theme) => {
                      const textLength = t(item.id).length;
                      return textLength > 20
                        ? { xs: "0.65rem", sm: "0.85rem" }
                        : { xs: "0.85rem", sm: "1rem" };
                    },
                    fontWeight: 900,
                    opacity: 0.85,
                    letterSpacing: "0.2px",
                    color: "#ffffff",
                    textAlign: "center",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: "1.1",
                    p: 0.5,
                  }}
                >
                  {t(item.id)}
                </Typography>
              </Link>
            </Grid>
          ))}
        </Grid>

        {/* Sponsors & Partners Button */}
        {/* <Link
          // href="/sponsors"
          sx={{
            display: "block",
            color: "#ffffff",
            textDecoration: "none",
            p: { xs: 2, sm: 2.5 },
            // border: "2px solid rgba(26,169,139, 1)",
            borderRadius: "8px",
            textAlign: "center",
            mb: { xs: 5, sm: 6 },
            backgroundColor: "rgba(76, 212, 192, 0.0)",
            // "&:hover": {
            //   backgroundColor: "rgba(76, 212, 192, 0.1)",
            // },
            fontSize: "1.2rem",
            fontWeight: 900,
          }}
        >
          {t("sponsorsAndPartners")}
        </Link> */}

        <SponsorSection />
      </Container>
    </Box>
  );
};

export default Homepage;
